import { Component } from '@angular/core';
import { Router } from '@angular/router';

interface BidStep {
  icon: string;
  title: string;
  description: string;
}

interface BidStatus {
  label: string;
  color: string;
  icon: string;
  description: string;
}

interface BidFaq {
  q: string;
  a: string;
}

@Component({
  selector: 'app-get-bid',
  templateUrl: './get-bid.component.html',
  styleUrls: ['./get-bid.component.css']
})
export class GetBidComponent {
  openFaq: number | null = null;

  steps: BidStep[] = [
    {
      icon: 'edit_note',
      title: 'Create Your Bid Request',
      description: 'Describe your project — scope, timeline, and budget — and add contact information for the contractor(s) you want to quote the work.'
    },
    {
      icon: 'groups',
      title: 'Add Contractor Contacts',
      description: 'Add one or more contacts per bid: name, title, email, and phone. Track every rep you speak with in a single place.'
    },
    {
      icon: 'track_changes',
      title: 'Track Status in Real Time',
      description: 'Each bid moves through a clear lifecycle — from Pending all the way to Won or Lost — so nothing falls through the cracks.'
    },
    {
      icon: 'check_circle',
      title: 'Award the Work',
      description: 'Once you select a contractor, mark the bid Won and archive the rest. Your bid history stays searchable for future reference.'
    }
  ];

  statuses: BidStatus[] = [
    {
      label: 'Pending',
      color: '#f59e0b',
      icon: 'schedule',
      description: 'Bid has been created and is waiting for contractor follow-up or additional details.'
    },
    {
      label: 'Submitted',
      color: '#3b82f6',
      icon: 'send',
      description: 'The bid has been sent to the contractor and you are awaiting their formal quote.'
    },
    {
      label: 'In Review',
      color: '#8b5cf6',
      icon: 'find_in_page',
      description: 'You have received the contractor\'s proposal and are evaluating it against other bids.'
    },
    {
      label: 'Won',
      color: '#10b981',
      icon: 'emoji_events',
      description: 'You selected this contractor. The project is ready to move into active management.'
    },
    {
      label: 'Lost',
      color: '#ef4444',
      icon: 'cancel',
      description: 'This contractor was not selected. Archived for future reference.'
    }
  ];

  faqs: BidFaq[] = [
    {
      q: 'How many contacts can I add to a single bid?',
      a: 'There is no limit. You can add as many contacts as needed — useful when a contractor has multiple reps, estimators, or project managers involved.'
    },
    {
      q: 'Can I edit a bid after it is submitted?',
      a: 'Yes — bids remain fully editable at any status. You can update the project details, bid amount, project type, or contacts at any time.'
    },
    {
      q: 'Can I have multiple bids for the same project?',
      a: 'Absolutely. Create one bid per contractor so you can compare amounts, contacts, and status side-by-side.'
    },
    {
      q: 'What happens to losing bids?',
      a: 'Bids marked Lost are archived but not deleted. You can review them, export contact info, or reopen them if a future opportunity arises.'
    },
    {
      q: 'Is bid data tied to a specific project?',
      a: 'Yes — each bid is linked to a project type category. When you move a bid to Won, you can then create or link the full project in the Project Management section.'
    }
  ];

  constructor(private router: Router) {}

  getStarted(): void {
    this.router.navigate(['/auth']);
  }

  goBack(): void {
    this.router.navigate(['/home']);
  }

  toggleFaq(i: number): void {
    this.openFaq = this.openFaq === i ? null : i;
  }
}
